import React, { useState } from "react";

import { Button, Stack, TableCell, TableRow, Typography } from "@mui/material";

import { Link, useNavigate } from "react-router-dom";

import {
  Meeting,
  PatientMeetingData,
  PatientSummary,
} from "../../../types";
import { RouteType } from "../../../router";

import {
  EditMeetingModal,
  MeetingInfoModal,
} from "../../../components/meeting-management";
import PatientChip from "../../../components/chips/patient-chip";
import EditablePatientStatusChip from "./editable-patient-status-chip";

import PatientMeetingSummaryTile from "./patient-meeting-summary-tile";
import { formatLongDate } from "./patient-meeting-date-format";
import CopyPatientIdButton from "./copy-patient-id-button";

interface PatientsTableRowProps {
  patient: PatientSummary;
}

const PatientsTableRow: React.FC<PatientsTableRowProps> = ({ patient }) => {
  const navigate = useNavigate();

  const [selectedMeeting, setSelectedMeeting] =
    useState<PatientMeetingData | null>(null);
  const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);

  const closeMeetingModal = () => {
    setSelectedMeeting(null);
  };

  const openEditMeetingModal = (meeting: Meeting) => {
    setEditingMeetingId(meeting.meetingId);
  };

  const closeEditMeetingModal = () => {
    setEditingMeetingId(null);
  };

  const creationDateString = patient.created
    ? formatLongDate(new Date(patient.created))
    : "???";

  const patientDetailsPath = `${RouteType.Patient}?patientId=${patient.patientId}`;

  return (
    <>
      <TableRow
        key={patient.displayId}
        sx={{
          cursor: "pointer",
          "&:hover": {
            background:
              "linear-gradient(135deg, rgba(5, 84, 128, 0.08) 0%, rgba(0, 150, 136, 0.08) 100%)",
          },
        }}
        onClick={() => navigate(patientDetailsPath)}
      >
        <TableCell align="left">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PatientChip label={`${patient.displayId || ""}`} size="medium" />
            <CopyPatientIdButton patientId={patient.displayId} />
          </Stack>
        </TableCell>
        <TableCell align="left">
          <Stack direction="row" onClick={(event) => event.stopPropagation()}>
            <EditablePatientStatusChip patient={patient} />
          </Stack>
        </TableCell>
        <TableCell align="left">
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: "0.8rem",
              lineHeight: 1.1,
              whiteSpace: "nowrap",
            }}
          >
            {creationDateString}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <PatientMeetingSummaryTile
            title="Last Meeting"
            isLoading={false}
            meeting={patient.lastMeeting}
            emptyLabel="No Past Meetings"
            onClick={(meeting) => setSelectedMeeting(meeting)}
          />
        </TableCell>
        <TableCell align="center">
          <PatientMeetingSummaryTile
            title="Next Meeting"
            isLoading={false}
            meeting={patient.nextMeeting}
            emptyLabel="Nothing Scheduled"
            onClick={(meeting) => setSelectedMeeting(meeting)}
          />
        </TableCell>
        <TableCell align="right">
          <Stack width={"100%"} direction="row" justifyContent={"flex-end"}>
            <Button
              variant="contained"
              size="small"
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                py: 0.5,
                borderRadius: 999,
              }}
              component={Link}
              to={patientDetailsPath}
              onClick={(event) => event.stopPropagation()}
            >
              <Stack direction={"row"}>View All Meetings</Stack>
            </Button>
          </Stack>
        </TableCell>
      </TableRow>

      {/* Meeting Info Modal */}
      {selectedMeeting && (
        <MeetingInfoModal
          open={selectedMeeting != null}
          handleClose={closeMeetingModal}
          meetingId={selectedMeeting.meetingId}
          onEditMeeting={openEditMeetingModal}
        />
      )}

      {editingMeetingId && (
        <EditMeetingModal
          open={editingMeetingId != null}
          handleClose={closeEditMeetingModal}
          meetingId={editingMeetingId}
        />
      )}
    </>
  );
};

export default PatientsTableRow;
